import { List } from 'immutable'
import { ImageBase, ImageOptions } from '../../../utils/image'
import { Link, Location } from '../cmsPageState'
import { BmiBlockState } from './block/bmiBlock/bmiBlockState'
import { ExpandBlockState } from './block/expandBlock/expandBlockState'
import { JotFormsBlockState } from './block/jotFormsBlock/jotform.state'
import { LocationExpandableBlockState } from './block/locationExpandbleBlok/locationExpandableBlockState'
import { NewsTeasersBlockState } from './block/newsTeasersBlock/newsTeasersBlockState'
import { PeopleBlockState } from './block/peopleBlock/peopleBlockState'
import { PhoneNumbersBlockState } from './block/phoneNumbersBlock/phoneNumbersBlockState'
import { RelatedContentBlockState } from './block/relatedContentBlock/relatedContentBlockState'
import { SpecialistsBlockState } from './block/specialistsBlock/specialistsBlockState'
import { WaitingTimesBlockState } from './block/waitingTimesBlock/waitingTimesBlockState'
import { AsyncState, Updater } from 'widgets-for-react'
import { SearchableDossierPage } from '../../../backendApi/generated'
import { DossierBlockState } from './block/dossierHighlightBlock/dossierHighlightBlock.state'

export type SPItem<title = string> = { title: title; id: number }

export type AdditionalBlockData = Partial<{
  department: SPItem
  subdepartment: SPItem
  centrum: SPItem
  subcentrum: SPItem
  location: Location
}>

export type BlocksState = List<Block>

export type Block = { index: number } & (
  | ({
      kind: 'ctaBlock'
    } & CtaBlock)
  | ({
      kind: 'expandBlock'
    } & ExpandBlock)
  | ({
      kind: 'expandBlockWithImage'
    } & ExpandBlock)
  | ({
      kind: 'featuredContentBlock'
    } & FeaturedContentBlock)
  | ({
      kind: 'linkBlock'
    } & LinkBlock)
  | ({
    kind: 'carouselBlock'
  } & CarouselBlock)
  |({
      kind: 'linkGridBlock'
    } & LinkGridBlock)
  | ({
      kind: 'linkGridCentraBlock'
    } & LinkGridBlock)
  | ({
      kind: 'mediaBlock'
    } & MediaBlock)
  | ({
      kind: 'quoteBlock'
    } & QuoteBlock)
  | ({
      kind: 'textImageBlock'
    } & TextImageBlock)
  | ({
      kind: 'textBlock'
    } & TextBlock)
  | ({
      kind: 'specialistsBlock'
    } & SpecialistsBlock)
  | ({
      kind: 'newsTeasersBlock'
    } & NewsTeasersBlock)
  | ({
      kind: 'phoneNumbersBlock'
    } & PhoneNumbersBlock)
  | ({
      kind: 'relatedContentBlock'
    } & RelatedContentBlock)
  | ({
      kind: 'locationBlock'
    } & LocationBlock)
  | ({
      kind: 'peopleBlock'
    } & PeopleBlock)
  | ({
      kind: 'locationExpandableBlock'
    } & LocationExpandableBlock)
  | ({
      kind: 'bmiBlock'
    } & BmiBlock)
  | ({
      kind: 'waitingTimesBlock'
    } & WaitingTimesBlock)
  | ({
      kind: 'jotFormBlock'
    } & JotFormBlock)
  | ({
      kind: 'dossierBlock'
    } & DossierBlock)
)

export type CtaBlock = {
  index: number
  title?: React.ReactNode
  intro: string
  displayOption: string
  image?: ImageBase & ImageOptions
  primaryAction?: Action
  secondaryAction?: Action
  isLinkType: boolean
  inview?: InviewOptions
}

export type HighlightBlock = {
  index: number
  title?: React.ReactNode
  intro: string
  label?: string
  image: ImageBase & ImageOptions
  primaryAction?: Link
  isLinkType: boolean
  inview?: InviewOptions
}

export type ExpandBlock = {
  index: number
  title?: React.ReactNode
  intro?: string,
  image?: ImageBase & ImageOptions
  displayGrid: boolean
  items: List<{
    title: string
    subtitle?: string
    text?: string
    SVGIcon?: SVGIconOptions
    nestedBlocks: List<NestedBlock>
  }>
  primaryAction?: Action
  inview?: InviewOptions
  expandBlockState: ExpandBlockState
}

export type NestedBlock =
  | ({
      kind: 'textBlockNested'
    } & TextBlockNested)
  | ({
      kind: 'quoteBlockNested'
    } & QuoteBlockNested)
  | ({
      kind: 'mediaBlockNested'
    } & MediaBlockNested)
  | ({
      kind: 'linkBlockNested'
    } & LinkBlockNested)

export type TextBlockNested = {
  index: number
  text: string
  inview?: InviewOptions
}

export type QuoteBlockNested = {
  index: number
  text: string
  author?: string
  inview?: InviewOptions
}

export type MediaBlockNested = {
  index: number
  items: {
    kind: 'image' | 'video'
    image?: ImageBase & ImageOptions
    video?: {
      url: string
      id: string
      type: string
    }
    caption?: string
    source?: string
  }[]
  inview?: InviewOptions
}

export type LinkBlockNested = {
  index: number
  items: List<LinkBlockNestedItem>
  SVGIcon?: SVGIconOptions
  inview?: InviewOptions
}

export type LinkBlockNestedItem = {
  url: string
  title: string
  text?: string
  displayGrid?: boolean
}

export type FeaturedContentBlock = {
  index: number
  imageLocation: 'left' | 'right'
  label?: string
  title: React.ReactNode
  intro: string
  primaryAction: Action
  image?: ImageBase & ImageOptions
  inview?: InviewOptions
}

export type LinkBlock = {
  index: number
  title?: React.ReactNode
  intro?: string
  items: List<Link & { text?: string }>
  SVGIcon?: SVGIconOptions
  inview?: InviewOptions
}

export type LinkGridBlock = {
  index: number
  withImages: boolean
  imageOptions?: ImageOptions
  items: LinkGridItem[]
  title?: React.ReactNode
  intro?: string
  itemsSVGIcon?: SVGIconOptions
  primaryAction?: Action
  inview?: InviewOptions
}

export type CarouselBlock = {
  index: number
  title?: React.ReactNode
  intro?: string
  items: CarouselItem[]
  inview?: InviewOptions
}

export type CarouselItem = {
  title: string
  intro: string
  link: Link
  image?: ImageBase & ImageOptions
}

export type LinkGridItem = {
  title: string
  label: string
  intro: string
  link: Link
  image?: ImageBase
}

export type MediaBlock = {
  index: number
  title?: React.ReactNode
  intro?: string
  items: {
    kind: 'image' | 'video'
    display: 'full-with' | 'content-width'
    image?: ImageBase & ImageOptions
    video?: {
      url: string
      id: string
      type: string
    }
    caption?: string
    source?: string
  }[]
  inview?: InviewOptions
}

export type QuoteBlock = {
  index: number
  text: string
  author: string
  primaryAction?: Action
  image?: ImageBase & ImageOptions
  inview?: InviewOptions
}

export type TextBlock = {
  index: number
  title?: React.ReactNode
  text: string
  primaryAction?: Action
  secondaryAction?: Action
  isLinkType: boolean
  inview?: InviewOptions
}

export type TextImageBlock = {
  index: number
  imageLocation: 'left' | 'right'
  image: ImageBase & ImageOptions
  label?: string
  title?: React.ReactNode
  text: string
  primaryAction?: Action
  secondaryAction?: Action
  isLinkType: boolean
  inview?: InviewOptions
}

export type SVGIconOptions = {
  width: string
  height: string
}

type Action = Link & { SVGIcon?: SVGIconOptions }

export type InviewOptions = {
  threshold?: number
  triggerOnce?: boolean
}

export type NewsTeasersBlock = {
  index: number
  inview?: InviewOptions
  title: string
  newsTeasersBlockState: NewsTeasersBlockState
}

export type SpecialistsBlock = {
  index: number
  inview?: InviewOptions
  title: string
  intro: string
  specialistsBlockState: SpecialistsBlockState
  imageOptions: ImageOptions
}

export type PhoneNumbersBlock = {
  index: number
  inview?: InviewOptions
  size: 'large' | 'small'
  descriptions: string[]
  phoneNumbersBlockState: PhoneNumbersBlockState
}

export type RelatedContentBlock = {
  index: number
  inview?: InviewOptions
  relatedContentBlockState: RelatedContentBlockState
  filter: RelatedContentFilter
}

export type RelatedContentFilter =
  | {
      source: 'department'
      department: string
    }
  | {
      source: 'specialist'
      specialist: string
    }
  | {
      source: 'tags'
      tags: string[]
    }

export type LocationBlock = {
  index: number
  inview?: InviewOptions
  description: string
  locationId: number
}

export type LocationExpandableBlock = {
  index: number
  inview?: InviewOptions
  title: string
  intro: string
  locationExpandableBlockState: LocationExpandableBlockState
}

export type PeopleBlock = {
  index: number
  inview?: InviewOptions
  title: React.ReactNode
  link: {
    title: string
    url: string
  }
  people: List<{
    name: string
    image: ImageBase
    email: string
    url: string
    phoneNumberId: number
  }>
  peopleBlockState: PeopleBlockState
  primaryAction?: Action
  imageOptions: ImageOptions
}

export type BmiBlock = {
  index: number
  inview?: InviewOptions
  bmiBlockState: BmiBlockState
}

export type WaitingTimesBlock = {
  index: number
  title?: React.ReactNode
  intro?: string
  prefixDepartment: string
  prefixGeneral: string
  suffixDepartment: string
  suffixGeneral: string
  spoedafspraak: string
  waitingTimesBlockState: WaitingTimesBlockState
}

export type JotFormBlock = {
  index: number
  title: string
  intro: string
  label: string
  useCookieBar: boolean
  cookiePlaceholder?: JSX.Element | string
  cookieButton?: string
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
  formID: string
  formTitle?: string
  image?: ImageBase & ImageOptions
  jotFormsBlockState: JotFormsBlockState
}

export type DossierBlock = {
  index: number
  title: string
  intro: string
  image: ImageBase & ImageOptions
  inview?: InviewOptions
  dossierName: string
  dossierSlug: string
  dossierPageId?: number
}

export const blocksStateUpdaters = {
  blockUpdater:
    (index: number) =>
    (updater: Updater<Block>) =>
    (currentState: BlocksState): BlocksState => {
      const blockStateItem = currentState.find((b) => b.index === index)
      return blockStateItem ? currentState.set(index, updater(blockStateItem)) : currentState
    },
  expandBlockStateUpdater:
    (updater: Updater<ExpandBlockState>) =>
    (currentState: Block): Block =>
      currentState.kind === 'expandBlock' || currentState.kind === 'expandBlockWithImage'
        ? {
            ...currentState,
            expandBlockState: updater(currentState.expandBlockState),
          }
        : currentState,
  specialistsBlockStateUpdater:
    (updater: Updater<SpecialistsBlockState>) =>
    (currentState: Block): Block =>
      currentState.kind === 'specialistsBlock'
        ? {
            ...currentState,
            specialistsBlockState: updater(currentState.specialistsBlockState),
          }
        : currentState,
  newsTeasersBlockStateUpdater:
    (updater: Updater<NewsTeasersBlockState>) =>
    (currentState: Block): Block =>
      currentState.kind === 'newsTeasersBlock'
        ? {
            ...currentState,
            newsTeasersBlockState: updater(currentState.newsTeasersBlockState),
          }
        : currentState,
  phoneNumbersBlockStateUpdater:
    (updater: Updater<PhoneNumbersBlockState>) =>
    (currentState: Block): Block =>
      currentState.kind === 'phoneNumbersBlock'
        ? {
            ...currentState,
            phoneNumbersBlockState: updater(currentState.phoneNumbersBlockState),
          }
        : currentState,
  relatedContentBlockStateUpdater:
    (updater: Updater<RelatedContentBlockState>) =>
    (currentState: Block): Block =>
      currentState.kind === 'relatedContentBlock'
        ? {
            ...currentState,
            relatedContentBlockState: updater(currentState.relatedContentBlockState),
          }
        : currentState,
  peopleBlockStateUpdater:
    (updater: Updater<PeopleBlockState>) =>
    (currentState: Block): Block =>
      currentState.kind === 'peopleBlock'
        ? {
            ...currentState,
            peopleBlockState: updater(currentState.peopleBlockState),
          }
        : currentState,
  locationExpandableBlockStateUpdater:
    (updater: Updater<LocationExpandableBlockState>) =>
    (currentState: Block): Block =>
      currentState.kind === 'locationExpandableBlock'
        ? {
            ...currentState,
            locationExpandableBlockState: updater(currentState.locationExpandableBlockState),
          }
        : currentState,
  bmiBlockStateUpdater:
    (updater: Updater<BmiBlockState>) =>
    (currentState: Block): Block =>
      currentState.kind === 'bmiBlock'
        ? {
            ...currentState,
            bmiBlockState: updater(currentState.bmiBlockState),
          }
        : currentState,
  waitingTimesBlockStateUpdater:
    (updater: Updater<WaitingTimesBlockState>) =>
    (currentState: Block): Block => {
      if (currentState.kind != 'waitingTimesBlock') return currentState
      const s1 = updater(currentState.waitingTimesBlockState)
      if (
        currentState.waitingTimesBlockState.consultationTimesState.kind != 'loaded' &&
        s1.consultationTimesState.kind == 'loaded'
      ) {
        s1.consultationTimesState.value = s1.consultationTimesState.value.filter((val) => val.time > 0)
      }
      return {
        ...currentState,
        waitingTimesBlockState: s1,
      }
    },
  jotFormsBlockStateUpdater:
    (updater: Updater<JotFormsBlockState>) =>
    (currentState: Block): Block =>
      currentState.kind === 'jotFormBlock'
        ? {
            ...currentState,
            jotFormsBlockState: updater(currentState.jotFormsBlockState),
          }
        : currentState,
  dossierBlockStateUpdater:
    (updater: Updater<DossierBlockState>) =>
    (currentState: Block): Block =>
      currentState.kind === 'dossierBlock' ? { ...currentState } : currentState
}
